import { useEffect, useMemo, useState } from "react";

import { css } from "@compiled/react";
import Fuse from "fuse.js";
import { useFilterableGalleryLoader } from "src/uniform/contexts/filterable-gallery-loader-context";

import ArrowRightIcon from "@atlaskit/icon/core/arrow-right";
import CrossIcon from "@atlaskit/icon/core/cross";
import SearchIcon from "@atlaskit/icon/core/search";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

import { isContentfulEnvelope } from "../../../../clients/uniform-client/enhancers/contentful-includes-enhancer";
import { GALLERY_SEARCH_EVENT } from "../../filterable-gallery/src/constants";
import {
    coerceToPrimitiveArray,
    resolveFieldPath
} from "../../filterable-gallery/src/utils/filterEngine";
import { dispatchShowAllFilterableGalleryItems } from "../../filterable-gallery-display-limit-override";

export { GALLERY_SEARCH_EVENT };

const SEARCH_QUERY_PARAM = "term";
const INVERSE_TEXT_COLOR = token("color.text.inverse", "#FFFFFF");

export interface TemplatesSearchBarProps {
    readonly heading?: string;
    readonly subheading?: string;
    readonly placeholder?: string;
    readonly items?: unknown;
    readonly suggestionLabelPath?: string;
    readonly suggestionProductPath?: string;
    readonly maxSuggestions?: number;
}

interface Suggestion {
    readonly id: string;
    readonly label: string;
    readonly queryValue: string;
}

interface SuggestionCandidate extends Suggestion {
    readonly searchableValues: string[];
}

const FUZZY_SEARCH_OPTIONS = {
    keys: ["searchableValues"],
    threshold: 0.4,
    ignoreLocation: true
};

const DEFAULT_PROPS = {
    heading: "Search all templates below",
    subheading: "Find Atlassian templates in one place.",
    placeholder: "Search templates",
    suggestionLabelPath: "fields.templateNameExternal",
    suggestionProductPath:
        "fields.product.0.fields.productNameReference.fields.productName",
    maxSuggestions: 6
} as const;

const TemplatesSearchBar = ({
    heading = DEFAULT_PROPS.heading,
    subheading = DEFAULT_PROPS.subheading,
    placeholder = DEFAULT_PROPS.placeholder,
    items: rawItems,
    suggestionLabelPath = DEFAULT_PROPS.suggestionLabelPath,
    suggestionProductPath = DEFAULT_PROPS.suggestionProductPath,
    maxSuggestions = DEFAULT_PROPS.maxSuggestions
}: TemplatesSearchBarProps): JSX.Element => {
    const [value, setValue] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const loaderContext = useFilterableGalleryLoader();
    const extraItems = loaderContext?.extraItems ?? [];
    const effectiveRawItems = extraItems.length > 0 ? extraItems : rawItems;

    const items = useMemo(
        () => normaliseItems(effectiveRawItems),
        [effectiveRawItems]
    );

    const suggestions = useMemo(
        () =>
            buildSuggestions({
                query: value,
                items,
                labelPath: suggestionLabelPath,
                productPath: suggestionProductPath,
                maxSuggestions
            }),
        [
            value,
            items,
            suggestionLabelPath,
            suggestionProductPath,
            maxSuggestions
        ]
    );

    const shouldShowSuggestions =
        isFocused && value.trim().length > 0 && suggestions.length > 0;

    useEffect(() => {
        if (typeof window === "undefined") return;

        const hydrateFromUrl = () => {
            const params = new URLSearchParams(window.location.search);
            setValue(params.get(SEARCH_QUERY_PARAM) ?? "");
        };

        hydrateFromUrl();
        window.addEventListener("popstate", hydrateFromUrl);
        return () => window.removeEventListener("popstate", hydrateFromUrl);
    }, []);

    const submitSearch = (nextValue = value) => {
        writeSearchQuery(nextValue);
        setIsFocused(false);
        setActiveIndex(-1);
    };

    const clearSearch = () => {
        setValue("");
        writeSearchQuery("");
        setActiveIndex(-1);
    };

    const submitAllSearchResults = () => {
        submitSearch();
        dispatchShowAllFilterableGalleryItems(value);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!shouldShowSuggestions) return;

        if (event.key === "ArrowDown") {
            event.preventDefault();
            setActiveIndex((current) =>
                Math.min(current + 1, suggestions.length - 1)
            );
            return;
        }

        if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex((current) => Math.max(current - 1, 0));
            return;
        }

        if (event.key === "Enter" && activeIndex >= 0) {
            event.preventDefault();
            const suggestion = suggestions[activeIndex];
            if (!suggestion) return;

            setValue(suggestion.queryValue);
            submitSearch(suggestion.queryValue);
        }
    };

    return (
        <section css={sectionStyle} data-testid="templates-search-bar">
            <h2
                className="templates-search-bar-heading"
                css={headingStyle}
                style={{ color: INVERSE_TEXT_COLOR }}
            >
                {heading}
            </h2>
            {subheading && (
                <p
                    className="templates-search-bar-subheading"
                    css={subheadingStyle}
                    style={{ color: INVERSE_TEXT_COLOR }}
                >
                    {subheading}
                </p>
            )}

            <form
                css={searchFormStyle}
                role="search"
                onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                        setIsFocused(false);
                    }
                }}
                onFocus={() => setIsFocused(true)}
                onSubmit={(event) => {
                    event.preventDefault();
                    submitAllSearchResults();
                }}
            >
                <div css={inputShellStyle}>
                    <span css={searchIconStyle} aria-hidden="true">
                        <SearchIcon label="" size="medium" />
                    </span>
                    <input
                        css={inputStyle}
                        aria-label={placeholder}
                        placeholder={placeholder}
                        value={value}
                        onChange={(event) => {
                            setValue(event.target.value);
                            setIsFocused(true);
                            setActiveIndex(-1);
                        }}
                        onKeyDown={handleKeyDown}
                    />
                    {value && (
                        <button
                            type="button"
                            css={clearButtonStyle}
                            aria-label="Clear search"
                            onClick={clearSearch}
                        >
                            <CrossIcon label="" size="medium" />
                        </button>
                    )}
                </div>

                {shouldShowSuggestions && (
                    <div
                        css={suggestionsPanelStyle}
                        data-testid="templates-search-suggestions"
                    >
                        <ul css={suggestionsListStyle}>
                            {suggestions.map((suggestion, index) => (
                                <li key={suggestion.id}>
                                    <button
                                        type="button"
                                        aria-label={suggestion.label}
                                        css={[
                                            suggestionButtonStyle,
                                            index === activeIndex
                                                ? activeSuggestionStyle
                                                : null
                                        ]}
                                        onClick={() => {
                                            setValue(suggestion.queryValue);
                                            submitSearch(suggestion.queryValue);
                                        }}
                                        onMouseDown={(event) =>
                                            event.preventDefault()
                                        }
                                        onMouseEnter={() =>
                                            setActiveIndex(index)
                                        }
                                        onFocus={() => setActiveIndex(index)}
                                    >
                                        <HighlightedText
                                            text={suggestion.label}
                                            query={value}
                                        />
                                    </button>
                                </li>
                            ))}
                        </ul>
                        <button
                            type="button"
                            css={allResultsButtonStyle}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={submitAllSearchResults}
                        >
                            All search results
                            <ArrowRightIcon label="" size="small" />
                        </button>
                    </div>
                )}
            </form>
        </section>
    );
};

const normaliseItems = (rawItems: unknown): unknown[] => {
    if (Array.isArray(rawItems)) return rawItems;
    if (isContentfulEnvelope(rawItems)) return rawItems.items;
    return [];
};

const resolveStrings = (item: unknown, path: string): string[] =>
    coerceToPrimitiveArray(resolveFieldPath(item, path));

const resolveString = (item: unknown, path: string): string | undefined =>
    resolveStrings(item, path)[0];

const buildSuggestions = ({
    query,
    items,
    labelPath,
    productPath,
    maxSuggestions
}: {
    query: string;
    items: unknown[];
    labelPath: string;
    productPath: string;
    maxSuggestions: number;
}): Suggestion[] => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return [];

    const lowerQuery = trimmedQuery.toLowerCase();
    const suggestionLimit = Math.max(maxSuggestions, 0);
    if (suggestionLimit === 0) return [];

    const suggestions: Suggestion[] = [
        {
            id: `query-${lowerQuery}`,
            label: trimmedQuery,
            queryValue: trimmedQuery
        }
    ];
    if (suggestionLimit === 1) return suggestions;

    const seen = new Set([lowerQuery]);
    const candidateKeys = new Set<string>();
    const candidates: SuggestionCandidate[] = [];

    for (const item of items) {
        const title = resolveString(item, labelPath);
        if (!title) continue;

        const product = resolveString(item, productPath);
        const label = product ? `${title} - ${product} Template` : title;
        const key = label.toLowerCase();
        if (seen.has(key) || candidateKeys.has(key)) continue;

        candidateKeys.add(key);
        candidates.push({
            id: key,
            label,
            queryValue: title,
            searchableValues: [
                label,
                ...resolveStrings(item, labelPath),
                ...resolveStrings(item, productPath)
            ]
        });
    }

    const matches = new Fuse(candidates, FUZZY_SEARCH_OPTIONS).search(
        trimmedQuery
    );

    for (const result of matches) {
        const suggestion = result.item;
        if (seen.has(suggestion.id)) continue;

        seen.add(suggestion.id);
        suggestions.push(suggestion);

        if (suggestions.length >= suggestionLimit) break;
    }

    return suggestions;
};

const writeSearchQuery = (query: string) => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const trimmedQuery = query.trim();
    if (trimmedQuery) {
        params.set(SEARCH_QUERY_PARAM, trimmedQuery);
    } else {
        params.delete(SEARCH_QUERY_PARAM);
    }

    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${
        nextSearch ? `?${nextSearch}` : ""
    }${window.location.hash}`;

    if (
        nextUrl !==
        `${window.location.pathname}${window.location.search}${window.location.hash}`
    ) {
        window.history.pushState({}, "", nextUrl);
        window.dispatchEvent(new Event("popstate"));
    }
    window.dispatchEvent(
        new CustomEvent(GALLERY_SEARCH_EVENT, {
            detail: { query: trimmedQuery }
        })
    );
};

const HighlightedText = ({
    text,
    query
}: {
    readonly text: string;
    readonly query: string;
}) => {
    const trimmedQuery = query.trim();
    const index = text.toLowerCase().indexOf(trimmedQuery.toLowerCase());

    if (!trimmedQuery || index < 0) return <>{text}</>;

    return (
        <>
            {text.slice(0, index)}
            <strong>{text.slice(index, index + trimmedQuery.length)}</strong>
            {text.slice(index + trimmedQuery.length)}
        </>
    );
};

const sectionStyle = css({
    backgroundColor: wacToken("color.primary.black"),
    color: wacToken("color.neutral.light0"),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    padding: "80px 24px",
    overflow: "visible",
    [media.below.sm]: {
        padding: "64px 16px"
    }
});

const headingStyle = css({
    color: `${INVERSE_TEXT_COLOR} !important`,
    margin: 0,
    fontSize: "2.5rem",
    lineHeight: "1.15",
    fontWeight: 700,
    [media.below.sm]: {
        fontSize: "2rem"
    }
});

const subheadingStyle = css({
    color: `${INVERSE_TEXT_COLOR} !important`,
    margin: `${token("space.200")} 0 0`,
    fontSize: "1.25rem",
    lineHeight: "1.4"
});

const searchFormStyle = css({
    position: "relative",
    width: "100%",
    maxWidth: "768px",
    marginBlockStart: "44px"
});

const inputShellStyle = css({
    width: "100%",
    height: "40px",
    borderRadius: "20px",
    backgroundColor: wacToken("color.neutral.light0"),
    display: "flex",
    alignItems: "center",
    padding: "0 16px 0 20px",
    color: wacToken("color.primary.black")
});

const searchIconStyle = css({
    color: token("color.icon.subtle"),
    display: "flex",
    alignItems: "center",
    flex: "0 0 auto",
    marginInlineEnd: token("space.150")
});

const inputStyle = css({
    border: 0,
    outline: 0,
    flex: "1 1 auto",
    minWidth: 0,
    backgroundColor: "transparent",
    color: wacToken("color.primary.black"),
    fontSize: "1rem",
    lineHeight: "1.5",
    fontFamily: "inherit"
});

const clearButtonStyle = css({
    background: "transparent",
    border: 0,
    color: wacToken("color.primary.black"),
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
    padding: token("space.050")
});

const suggestionsPanelStyle = css({
    position: "absolute",
    zIndex: 5,
    insetBlockStart: "56px",
    insetInlineStart: 0,
    width: "100%",
    backgroundColor: wacToken("color.neutral.light0"),
    color: wacToken("color.primary.black"),
    borderRadius: "20px",
    padding: "26px 28px 22px",
    textAlign: "left",
    border: `1px solid ${token("color.border.selected")}`,
    boxShadow: token("elevation.shadow.overlay")
});

const suggestionsListStyle = css({
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: token("space.100")
});

const suggestionButtonStyle = css({
    width: "100%",
    border: 0,
    backgroundColor: "transparent",
    color: wacToken("color.primary.black"),
    cursor: "pointer",
    display: "block",
    textAlign: "left",
    padding: `${token("space.050")} ${token("space.100")}`,
    minHeight: "32px",
    fontSize: "1rem",
    lineHeight: "1.5",
    fontFamily: "inherit"
});

const activeSuggestionStyle = css({
    backgroundColor: wacToken("color.neutral.light200")
});

const allResultsButtonStyle = css({
    "border": 0,
    "backgroundColor": "transparent",
    "color": token("color.link"),
    "cursor": "pointer",
    "display": "inline-flex",
    "alignItems": "center",
    "gap": token("space.050"),
    "marginBlockStart": token("space.200"),
    "padding": `${token("space.050")} ${token("space.100")}`,
    "fontSize": "1rem",
    "fontWeight": 600,
    "fontFamily": "inherit",
    "&:hover, &:focus-visible": {
        backgroundColor: wacToken("color.neutral.light200"),
        outline: 0
    }
});

export default TemplatesSearchBar;
